<template>
  <div>
    <!-- Load Study modal -->
    <div v-if="showLoadModal" class="fixed inset-0 z-40 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/30" @click="showLoadModal = false" />
      <div class="relative z-10 bg-white rounded-lg shadow-xl w-[56rem] max-w-[90vw] h-[80vh] flex flex-col">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span class="text-sm font-semibold text-gray-900">Load Study</span>
          <button @click="showLoadModal = false" class="text-gray-400 hover:text-gray-600">&times;</button>
        </div>
        <div class="overflow-y-auto flex-1">
          <div class="grid grid-cols-[minmax(0,1fr)_14rem_14rem] gap-x-4 px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200 sticky top-0 bg-white">
            <span>Name</span>
            <span>Template</span>
            <span>Population</span>
          </div>
          <div class="divide-y divide-gray-100">
            <div
              v-for="study in studies"
              :key="study.id"
              @click="pickStudy(study)"
              :class="[
                'grid grid-cols-[minmax(0,1fr)_14rem_14rem] gap-x-4 px-4 py-1.5 cursor-pointer items-baseline',
                selectedStudyId === study.id ? 'bg-blue-50' : 'hover:bg-gray-50'
              ]"
            >
              <span class="text-xs text-gray-900 truncate">
                <svg class="inline w-3.5 h-3.5 mr-1.5 -mt-0.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>{{ study.name }}
              </span>
              <span class="text-xs font-mono text-gray-500 truncate">{{ study.template_key || '—' }}</span>
              <span class="text-xs text-gray-500 truncate">{{ study.population_name }}</span>
            </div>
          </div>
          <div v-if="!studies.length" class="px-4 py-8 text-center text-sm text-gray-400">No studies yet</div>
        </div>
      </div>
    </div>

    <!-- Data Attribute picker modal -->
    <div v-if="daPicker" class="fixed inset-0 z-40 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/30" @click="daPicker = null" />
      <div class="relative z-10 bg-white rounded-lg shadow-xl w-[56rem] max-w-[90vw] h-[80vh] flex flex-col">
        <div class="px-4 py-3 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <span class="text-sm font-semibold text-gray-900">{{ daPicker.title }}</span>
            <button @click="daPicker = null" class="text-gray-400 hover:text-gray-600">&times;</button>
          </div>
          <p class="text-xs text-gray-500 mt-0.5">{{ daPicker.subtitle }}</p>
          <input
            ref="daSearchInput"
            v-model="daSearch"
            placeholder="Search attributes..."
            class="mt-2 w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
          />
        </div>
        <div class="overflow-y-auto flex-1">
          <div class="grid grid-cols-[minmax(0,1fr)_8rem_minmax(0,1.4fr)] gap-x-4 px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200 sticky top-0 bg-white">
            <span>Name</span>
            <span>Type</span>
            <span>Description</span>
          </div>
          <div class="divide-y divide-gray-100">
            <div
              v-for="option in daPickerOptions"
              :key="option.id"
              @click="pickDa(option)"
              :class="[
                'grid grid-cols-[minmax(0,1fr)_8rem_minmax(0,1.4fr)] gap-x-4 px-4 py-1.5 cursor-pointer items-baseline',
                option.name === daPicker.current ? 'bg-blue-50' : 'hover:bg-gray-50'
              ]"
            >
              <span class="text-xs text-gray-900 truncate">{{ option.name }}</span>
              <span class="text-xs font-mono text-gray-500 truncate">{{ option.attribute_type || '' }}</span>
              <span class="text-xs text-gray-500 truncate">{{ option.description || '' }}</span>
            </div>
          </div>
          <div v-if="!daPickerOptions.length" class="px-4 py-8 text-center text-sm text-gray-400">No matches</div>
        </div>
      </div>
    </div>

    <!-- The study — script front and center, frame/params beside it -->
    <div class="space-y-4 min-w-0">
      <!-- Top bar -->
      <div class="flex items-center gap-3">
        <button @click="openLoadModal" class="btn-primary-sm shrink-0">Load</button>
        <button @click="newStudy" class="btn-primary-sm shrink-0">+ New</button>
        <input
          v-model="config.name"
          type="text"
          placeholder="Study name..."
          class="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium"
        />
        <span class="text-xs font-mono px-2 py-1 bg-gray-900 text-gray-100 rounded">R</span>
        <span v-if="config.template_key" class="text-xs font-mono px-2 py-1 bg-gray-100 text-gray-700 rounded">{{ config.template_key }}</span>
        <span v-if="config.is_builtin" class="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">Built-in</span>
        <span v-if="saveError" class="text-xs text-red-600 max-w-xs truncate" :title="saveError">{{ saveError }}</span>
        <span v-if="hasAnyErrors" class="text-xs text-red-600">Validation errors</span>
        <button @click="saveStudy" :disabled="saving || !config.name" class="btn-primary">
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
        <button
          @click="runStudy"
          :disabled="!canRun || running || hasAnyErrors"
          class="btn-primary"
        >
          {{ running ? runPhase : 'Run Study' }}
        </button>
      </div>

      <div class="flex gap-4 items-start">
        <!-- Script: the study itself -->
        <div class="flex-1 min-w-0">
          <div class="border border-gray-300 rounded overflow-hidden">
            <div class="flex items-center justify-between px-3 py-1.5 bg-gray-900">
              <span class="text-xs font-mono text-gray-400">script.R</span>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500 hidden lg:inline">ks_frame() &middot; ks_params() &middot; ks_effect() &middot; ks_plot()</span>
                <select v-model="selectedTemplate" class="px-2 py-0.5 border border-gray-700 bg-gray-800 text-gray-200 rounded text-xs">
                  <option value="">Templates...</option>
                  <option v-for="t in templates" :key="t.key" :value="t.key">{{ t.key }}</option>
                </select>
                <button @click="applyTemplate" :disabled="!selectedTemplate" class="text-xs px-2 py-0.5 bg-primary-500 hover:bg-primary-600 text-white rounded">Apply</button>
              </div>
            </div>
            <textarea
              v-model="config.script"
              rows="30"
              spellcheck="false"
              class="w-full px-4 py-3 bg-gray-900 text-gray-100 text-xs font-mono leading-relaxed focus:outline-none resize-y"
              placeholder="# The study's R script.
# Reads the materialized frame via ks_frame(), params via ks_params().
# Emits results via ks_effect() / ks_table() / ks_plot() / ks_diagnostic().
# Pick a template above to start."
            ></textarea>
          </div>
        </div>

        <!-- Frame & Params: what the script consumes -->
        <div class="w-80 shrink-0 space-y-3">
          <div v-if="!frameSpecs.length && !paramRows.length" class="border border-gray-200 rounded p-3 text-xs text-gray-400">
            The script defines what appears here — its ks_frame() declarations and its p &lt;- list(...) block generate the Frame and Params panels, and edits here rewrite the script.
          </div>
          <div v-for="spec in frameSpecs" :key="spec.slug" class="border border-gray-200 rounded p-3 space-y-1.5">
            <div class="text-xs font-semibold text-gray-700">{{ spec.slug }}.csv</div>
            <button
              @click="openDaPicker(spec)"
              class="w-full px-1.5 py-1 border border-gray-300 rounded text-[11px] text-left truncate bg-white hover:border-gray-400"
              :class="spec.found ? 'text-gray-900' : 'border-red-300 bg-red-50 text-red-700'"
              :title="spec.found ? spec.attribute_name : `no DataAttribute matches '${spec.slug}'`"
            >{{ spec.attribute_name || spec.slug }}</button>
            <div v-if="spec.columns.length" class="flex flex-wrap gap-1">
              <code v-for="col in spec.columns" :key="col" class="text-[10px] text-gray-500">{{ col }}</code>
            </div>
          </div>

          <div v-if="paramRows.length" class="border border-gray-200 rounded p-3 space-y-3">
            <div class="text-xs font-semibold text-gray-700">Params <span class="font-normal text-gray-400">&middot; the script's p &lt;- list(...)</span></div>
            <div v-for="param in paramRows" :key="param.name" class="flex items-center gap-2">
              <code class="text-[11px] text-gray-700 w-28 shrink-0 truncate" :title="'p$' + param.name">{{ param.name }}</code>
              <input
                v-if="param.raw === 'TRUE' || param.raw === 'FALSE'"
                type="checkbox"
                :checked="param.raw === 'TRUE'"
                @change="setParamValue(param.name, $event.target.checked ? 'TRUE' : 'FALSE')"
                class="rounded"
              />
              <input
                v-else
                :value="param.raw"
                @change="setParamValue(param.name, $event.target.value)"
                class="flex-1 min-w-0 px-1.5 py-1 border border-gray-300 rounded text-[11px] font-mono"
                title="an R literal: 5, &quot;text&quot;, TRUE, c(age = 5, ...)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div v-if="results" class="border border-gray-200 rounded p-4 space-y-4">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium">Results</span>
          <span class="text-xs text-gray-500">{{ formatDate(results.run_at) }} · {{ results.status }}</span>
        </div>

        <div v-if="results.status === 'failed'" class="text-sm text-red-600 whitespace-pre-wrap">{{ results.error_message }}</div>
        <div v-else-if="results.status === 'pending' || results.status === 'running'" class="text-sm text-gray-500">
          Running — building frame and executing script...
        </div>

        <!-- Effects -->
        <div v-if="scriptEffects.length" class="grid grid-cols-2 gap-3">
          <div v-for="e in scriptEffects" :key="e.name" class="border border-gray-200 rounded p-3">
            <div class="text-xs text-gray-500">{{ e.name }}</div>
            <div class="text-xl font-semibold" :class="e.p < 0.05 ? 'text-green-700' : 'text-gray-900'">
              {{ formatEffectValue(e) }}
            </div>
            <div class="text-xs text-gray-500">
              95% CI {{ formatEffectNum(e.ci_low, e.unit) }} to {{ formatEffectNum(e.ci_high, e.unit) }}
              · p={{ e.p < 0.001 ? '<0.001' : e.p?.toFixed(3) }}
            </div>
            <div class="text-xs text-gray-400 mt-1">{{ e.model }}</div>
          </div>
        </div>

        <!-- Diagnostics -->
        <div v-if="scriptDiagnostics.length" class="text-xs text-gray-600 space-y-1">
          <div v-for="d in scriptDiagnostics" :key="d.name">
            <span class="font-medium">{{ d.name }}:</span> {{ d.detail }}
          </div>
        </div>

        <!-- Plots -->
        <div v-if="results.artifacts?.length" class="grid grid-cols-2 gap-4">
          <div v-for="a in results.artifacts" :key="a.name" class="border border-gray-100 rounded p-2">
            <div class="text-xs font-medium text-gray-700 mb-1">{{ a.display_name || a.name }}</div>
            <img :src="artifactUrl(a)" :alt="a.display_name || a.name" class="w-full" />
            <div v-if="a.caption" class="text-xs text-gray-400 mt-1">{{ a.caption }}</div>
          </div>
        </div>

        <!-- Tables -->
        <div v-for="t in scriptTables" :key="t.name" class="overflow-x-auto">
          <div class="text-xs font-medium text-gray-700 mb-1">{{ t.name }}</div>
          <table class="min-w-full text-xs border border-gray-200">
            <thead>
              <tr class="bg-gray-50">
                <th v-for="c in t.columns" :key="c" class="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{{ c }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in t.rows" :key="i" class="border-b border-gray-100">
                <td v-for="c in t.columns" :key="c" class="px-2 py-1">{{ formatCell(row[c]) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="t.caption" class="text-xs text-gray-400 mt-1">{{ t.caption }}</div>
        </div>

        <!-- Console -->
        <details v-if="results.console_output">
          <summary class="text-xs text-gray-500 cursor-pointer">Console output</summary>
          <pre class="sensitive text-xs bg-gray-900 text-gray-100 rounded p-3 mt-1 overflow-x-auto max-h-64" data-demo-blur>{{ results.console_output }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'StudyBuilder',
  props: {
    attributes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      studies: [],
      templates: [],
      selectedTemplate: '',
      selectedStudyId: null,
      loading: true,
      running: false,
      runPhase: 'Running...',
      saving: false,
      saveError: null,
      showLoadModal: false,
      frameSpecs: [],
      frameSpecTimer: null,
      daPicker: null,
      daSearch: '',

      results: null,
      pollTimer: null,
      config: this.emptyConfig(),
    };
  },
  computed: {
    canRun() {
      return Boolean(this.config.script);
    },
    hasAnyErrors() {
      return this.frameSpecs.some(s => !s.found);
    },
    scriptEffects() {
      return this.results?.results?.effects || [];
    },
    scriptTables() {
      return this.results?.results?.tables || [];
    },
    scriptDiagnostics() {
      return this.results?.results?.diagnostics || [];
    },
    daPickerOptions() {
      if (!this.daPicker) return [];
      const q = this.daSearch.trim().toLowerCase();
      if (!q) return this.attributes;
      return this.attributes.filter(o =>
        o.name.toLowerCase().includes(q) ||
        (o.description || '').toLowerCase().includes(q));
    },
    paramRows() {
      const script = this.config.script || '';
      const m = /\bp\s*<-\s*list\s*\(/g.exec(script);
      if (!m) return [];
      const span = this.findCallSpan(script.slice(m.index), /^p\s*<-\s*list\s*\(/);
      if (!span) return [];
      const body = script.slice(m.index + span.start, m.index + span.end);
      const rows = [];
      const re = /([\w.]+)\s*=\s*("(?:[^"\\]|\\.)*"|c\([^)]*\)|[^,\n)]+)/g;
      let am;
      while ((am = re.exec(body))) {
        rows.push({ name: am[1], raw: am[2].trim() });
      }
      return rows;
    },
  },
  watch: {
    'config.script'() {
      if (this.frameSpecTimer) clearTimeout(this.frameSpecTimer);
      this.frameSpecTimer = setTimeout(this.fetchFrameSpecs, 600);
    },
  },
  methods: {
    emptyConfig() {
      return {
        name: '',
        is_builtin: false,
        script: '',
        template_key: '',
      };
    },
    savePayload() {
      return {
        name: this.config.name,
        script: this.config.script,
        script_language: 'r',
        template_key: this.config.template_key,
      };
    },
    async saveStudy() {
      this.saving = true;
      this.saveError = null;
      try {
        let response;
        if (this.selectedStudyId) {
          response = await axios.put(`api/impact_analytics/studies/${this.selectedStudyId}/`, this.savePayload());
        } else {
          response = await axios.post('api/impact_analytics/studies/', this.savePayload());
          this.selectedStudyId = response.data.id;
        }
        await this.fetchStudies();
        return true;
      } catch (error) {
        const detail = error.response?.data;
        this.saveError = typeof detail === 'object'
          ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join('; ').slice(0, 200)
          : 'Save failed';
        return false;
      } finally {
        this.saving = false;
      }
    },
    async fetchTemplates() {
      try {
        const response = await axios.get('api/impact_analytics/study-templates/');
        this.templates = response.data;
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    },
    applyFlagshipTemplate() {
      const t = this.templates.find(x => x.key === 'matched_did_aligned') || this.templates[0];
      if (!t) return;
      this.config.script = t.script;
      this.config.template_key = t.key;
    },
    applyTemplate() {
      const t = this.templates.find(x => x.key === this.selectedTemplate);
      if (!t) return;
      if (this.config.script && this.config.script !== t.script) {
        const current = this.templates.find(x => x.key === this.config.template_key);
        const edited = !current || this.config.script !== current.script;
        if (edited && !confirm(`Replace the current script with the ${t.key} template? Unsaved edits to the script will be lost.`)) {
          return;
        }
      }
      this.config.script = t.script;
      this.config.template_key = t.key;
    },
    artifactUrl(a) {
      return `/api/impact_analytics/study-runs/${this.results.id}/artifact/?name=${encodeURIComponent(a.name)}`;
    },
    formatEffectNum(v, unit) {
      if (v === null || v === undefined) return '-';
      const n = Number(v);
      const formatted = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: Math.abs(n) < 10 ? 2 : 0 });
      const sign = n < 0 ? '-' : '';
      return unit === 'dollars' ? `${sign}$${formatted}` : `${sign}${formatted}`;
    },
    formatEffectValue(e) {
      return this.formatEffectNum(e.estimate, e.unit);
    },
    formatCell(v) {
      if (v === null || v === undefined) return '';
      if (typeof v === 'number' && !Number.isInteger(v)) return v.toFixed(Math.abs(v) < 1 ? 3 : 1);
      return v;
    },
    pollRun(runId) {
      this.pollTimer = setInterval(async () => {
        try {
          const response = await axios.get(`api/impact_analytics/study-runs/${runId}/`);
          this.results = response.data;
          if (['completed', 'failed'].includes(response.data.status)) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.running = false;
          }
        } catch (error) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
          this.running = false;
        }
      }, 2500);
    },
    async fetchStudies() {
      try {
        const response = await axios.get('api/impact_analytics/studies/');
        this.studies = response.data;
      } catch (error) {
        console.error('Error fetching studies:', error);
      }
    },
    newStudy() {
      this.selectedStudyId = null;
      this.config = this.emptyConfig();
      this.paramsError = null;
      this.results = null;
      this.applyFlagshipTemplate();
    },
    openLoadModal() {
      this.fetchStudies();
      this.showLoadModal = true;
    },
    pickStudy(study) {
      this.showLoadModal = false;
      this.loadStudy(study);
    },
    async loadStudy(study) {
      this.selectedStudyId = study.id;
      this.results = null;

      // Fetch full study details
      try {
        const response = await axios.get(`api/impact_analytics/studies/${study.id}/`);
        const s = response.data;

        this.config = {
          name: s.name,
          is_builtin: s.is_builtin,
          script: s.script || '',
          template_key: s.template_key || '',
        };

        this.fetchFrameSpecs();
      } catch (error) {
        console.error('Error loading study details:', error);
      }
    },
    openDaPicker(spec) {
      this.daPicker = {
        title: `Input ${spec.slug}.csv`,
        subtitle: 'One DataAttribute is one input: its SQL runs and its output is written verbatim to this CSV for the script to read.',
        current: spec.attribute_name,
        slug: spec.slug,
      };
      this.daSearch = '';
      this.$nextTick(() => this.$refs.daSearchInput?.focus());
    },
    pickDa(option) {
      const newSlug = this.slugifyName(option.name);
      this.setFrameSlug(this.daPicker.slug, newSlug);
      this.daPicker = null;
    },
    slugifyName(name) {
      const slug = (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      return /^[0-9]/.test(slug) ? `x${slug}` : slug;
    },
    setFrameSlug(oldSlug, newSlug) {
      if (oldSlug === newSlug) return;
      this.config.script = this.config.script.replace(
        new RegExp(`(ks_frame\\(\\s*")${oldSlug}(")`), `$1${newSlug}$2`);
    },
    findCallSpan(script, headRe) {
      const m = headRe.exec(script);
      if (!m) return null;
      let i = m.index + m[0].length;
      let depth = 1;
      let quote = null;
      const start = i;
      while (i < script.length && depth) {
        const ch = script[i];
        if (quote) {
          if (ch === quote && script[i - 1] !== '\\') quote = null;
        } else if (ch === '"' || ch === "'") {
          quote = ch;
        } else if (ch === '(') {
          depth += 1;
        } else if (ch === ')') {
          depth -= 1;
        }
        i += 1;
      }
      return depth === 0 ? { start, end: i - 1 } : null;
    },
    setParamValue(name, raw) {
      const script = this.config.script;
      const head = /\bp\s*<-\s*list\s*\(/g;
      const m = head.exec(script);
      if (!m) return;
      const span = this.findCallSpan(script.slice(m.index), /^p\s*<-\s*list\s*\(/);
      if (!span) return;
      const abs = { start: m.index + span.start, end: m.index + span.end };
      const body = script.slice(abs.start, abs.end);
      const re = new RegExp(`(\\b${name}\\s*=\\s*)("(?:[^"\\\\]|\\\\.)*"|c\\([^)]*\\)|[^,\\n)]+)`);
      if (!re.test(body)) return;
      const newBody = body.replace(re, `$1${raw}`);
      this.config.script = script.slice(0, abs.start) + newBody + script.slice(abs.end);
    },
    async fetchFrameSpecs() {
      if (!this.config.script) {
        this.frameSpecs = [];
        return;
      }
      try {
        const response = await axios.post('api/impact_analytics/study-frame-spec/', {
          script: this.config.script,
        });
        this.frameSpecs = response.data;
      } catch (error) {
        console.error('Error resolving frame spec:', error);
      }
    },
    getAttributeName(id) {
      const attr = this.attributes.find(a => a.id === id);
      return attr ? attr.name : '';
    },
    async runStudy() {
      if (!this.canRun || this.hasAnyErrors) return;
      this.running = true;
      this.runPhase = 'Saving...';
      this.results = null;

      const saved = await this.saveStudy();
      if (!saved) {
        this.running = false;
        return;
      }

      this.runPhase = 'Running...';
      try {
        const response = await axios.post(`api/impact_analytics/studies/${this.selectedStudyId}/run/`, {});
        this.results = response.data;
        if (['pending', 'running'].includes(response.data.status)) {
          this.pollRun(response.data.id);
        } else {
          this.running = false;
        }
      } catch (error) {
        console.error('Error running study:', error);
        this.saveError = error.response?.data?.error || error.message;
        this.running = false;
      }
    },
    formatDate(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleString();
    },
  },
  mounted() {
    this.fetchStudies();
    this.fetchTemplates();
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
};
</script>
